import React, { useState } from 'react';
import Card from '../UI/Card';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { formatEthiopianDateTime } from '../../utils/ethiopianDateTime';

interface FinancialTransaction {
  id: string;
  type: 'profit' | 'expense' | 'income';
  amount: number;
  paidTo: string;
  description: string;
  referenceId: string;
  periodFrom: string;
  periodTo: string;
  netAccountBalanceAfter: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface FinancialTableProps {
  transactions: FinancialTransaction[];
  itemsPerPage?: number;
  pagination?: { page: number; limit: number; total: number; pages: number };
  onPageChange?: (page: number) => void;
  onTypeFilterChange?: (type: string) => void;
  typeFilter?: string;
  onSearch?: () => void;
  onSearchChange?: (value: string) => void;
  onSearchKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  searchValue?: string;
  onClearFilters?: () => void;
  loading?: boolean;
  hasFilters?: boolean;

  fromDate?: string;
  fromTime?: string;
  toDate?: string;
  toTime?: string;
  onFromDateChange?: (v: string) => void;
  onFromTimeChange?: (v: string) => void;
  onToDateChange?: (v: string) => void;
  onToTimeChange?: (v: string) => void;
  onApplyDateFilter?: () => void;
  onClearDateFilter?: () => void;
}

const FinancialTable: React.FC<FinancialTableProps> = ({
  transactions,
  pagination = { page: 1, limit: 10, total: 0, pages: 0 },
  onPageChange,
  onTypeFilterChange,
  typeFilter = 'all',
  onSearch,
  onSearchChange,
  onSearchKeyDown,
  searchValue = '',
  onClearFilters,
  loading = false,
  hasFilters = false,

  fromDate = '',
  fromTime = '',
  toDate = '',
  toTime = '',
  onFromDateChange,
  onFromTimeChange,
  onToDateChange,
  onToTimeChange,
  onApplyDateFilter,
  onClearDateFilter,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);

  const getPageNumbers = (): number[] => {
    const arr: number[] = [];
    const total = pagination.pages;
    const current = pagination.page;
    if (total <= 5) for (let i = 1; i <= total; i++) arr.push(i);
    else if (current <= 3) for (let i = 1; i <= 5; i++) arr.push(i);
    else if (current >= total - 2) for (let i = total - 4; i <= total; i++) arr.push(i);
    else for (let i = current - 2; i <= current + 2; i++) arr.push(i);
    return arr;
  };

  const getTypeColor = (type: string) => {
    if (type === 'profit') return 'bg-green-100 text-green-700';
    if (type === 'income') return 'bg-blue-100 text-blue-700';
    return 'bg-red-100 text-red-700';
  };

  const getAmountColor = (type: string) => {
    if (type === 'profit') return 'text-green-600';
    if (type === 'income') return 'text-blue-600';
    return 'text-red-600';
  };

  const getTypeLabel = (v: string) => {
    if (v === 'profit') return 'Profit';
    if (v === 'expense') return 'Expense';
    if (v === 'income') return 'Income';
    return 'All Types';
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;

      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');

      return formatEthiopianDateTime(`${y}-${m}-${day}`, `${hh}:${mm}`);
    } catch {
      return dateStr;
    }
  };

  const startIndex = (pagination.page - 1) * pagination.limit;
  const dateFilterActive = !!(fromDate || toDate);

  return (
    <Card>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search by paid to, description, reference..."
              value={searchValue}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              onKeyDown={onSearchKeyDown}
              className="w-full px-4 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              onClick={onSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600"
            >
              <MagnifyingGlassIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border flex items-center gap-2 whitespace-nowrap ${
                typeFilter !== 'all'
                  ? 'bg-primary-50 text-primary-700 border-primary-300 hover:bg-primary-100'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span>{getTypeLabel(typeFilter)}</span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsDropdownOpen(false)}
                />
                <div className="absolute left-0 mt-2 w-44 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                  {['all', 'income', 'profit', 'expense'].map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        onTypeFilterChange && onTypeFilterChange(option);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ${
                        typeFilter === option
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${
                          option === 'income'
                            ? 'bg-blue-500'
                            : option === 'profit'
                            ? 'bg-green-500'
                            : option === 'expense'
                            ? 'bg-red-500'
                            : 'bg-gray-400'
                        }`}
                      />
                      {getTypeLabel(option)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <button
            onClick={() => setShowDateFilter((v) => !v)}
            className={`px-3 py-2 text-sm font-medium rounded-lg border flex items-center gap-2 whitespace-nowrap ${
              dateFilterActive
                ? 'bg-primary-50 text-primary-700 border-primary-300'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <CalendarDaysIcon className="w-4 h-4" />
            Period
            {dateFilterActive && (
              <span className="w-2 h-2 rounded-full bg-primary-500" />
            )}
          </button>

          {hasFilters && (
            <button
              onClick={onClearFilters}
              className="flex items-center gap-1 px-3 py-2 text-sm text-secondary-500 hover:text-secondary-600 font-medium"
            >
              <XMarkIcon className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>

        <div className="text-sm text-gray-500 whitespace-nowrap">
          Showing {transactions.length > 0 ? startIndex + 1 : 0} to{' '}
          {Math.min(startIndex + pagination.limit, pagination.total)} of{' '}
          {pagination.total} transactions
        </div>
      </div>

      {/* Date + time filter row */}
      {showDateFilter && (
        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-lg border border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600">
              Period From
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => onFromDateChange && onFromDateChange(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-900"
            />
            <input
              type="time"
              value={fromTime}
              onChange={(e) => onFromTimeChange && onFromTimeChange(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-900"
            />
          </div>
          <span className="text-gray-500">→</span>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600">
              Period To
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => onToDateChange && onToDateChange(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-900"
            />
            <input
              type="time"
              value={toTime}
              onChange={(e) => onToTimeChange && onToTimeChange(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-900"
            />
          </div>

          <button
            onClick={onApplyDateFilter}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600"
          >
            Apply
          </button>
          {dateFilterActive && (
            <button
              onClick={onClearDateFilter}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-100"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Period From
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Period To
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Net After
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Paid To
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Description
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Reference
                </th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">
                  Logged
                </th>
              </tr>
            </thead>
            <tbody>
              {transactions.length > 0 ? (
                transactions.map((t) => (
                  <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2.5 px-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getTypeColor(
                          t.type
                        )}`}
                      >
                        {t.type === 'profit'
                          ? 'Profit'
                          : t.type === 'income'
                          ? 'Income'
                          : 'Expense'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`text-sm font-medium ${getAmountColor(t.type)}`}>
                        ETB {t.amount.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-700 whitespace-nowrap">
                      {formatDateTime(t.periodFrom)}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-700 whitespace-nowrap">
                      {formatDateTime(t.periodTo)}
                    </td>
                    <td className="py-2.5 px-3 text-sm font-medium tabular-nums text-gray-900 whitespace-nowrap">
                      ETB {(t.netAccountBalanceAfter ?? 0).toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 text-sm text-gray-600">{t.paidTo}</td>
                    <td
                      className="py-2.5 px-3 text-sm text-gray-600 max-w-[180px] truncate"
                      title={t.description}
                    >
                      {t.description}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-500 font-mono">
                      {t.referenceId}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-gray-500 whitespace-nowrap">
                      {formatDateTime(t.createdAt)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-sm text-gray-500">
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && !loading && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.pages}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => onPageChange && onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Previous
            </button>
            {getPageNumbers().map((n) => (
              <button
                key={n}
                onClick={() => onPageChange && onPageChange(n)}
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  pagination.page === n
                    ? 'bg-primary-500 text-white shadow-md'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => onPageChange && onPageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
            >
              Next
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default FinancialTable;